"use client";

import type { ComponentProps, ReactNode } from "react";
import { cn } from "@repo/utils/cn";

/**
 * Admin-density primitives for the AI image labelling page.
 *
 * Colour, radius and type come entirely from the `--admin-*` custom properties
 * that `aiImages.css` maps onto the host brand's design tokens, so this file is
 * identical in `apps/web` and `apps/web-parkmobile` and neither brand's colours
 * are hardcoded here.
 *
 * Deliberately not the marketing primitives from `@repo/ui` / `@repo/ui-parkmobile`:
 * those are sized for a page (a 56px pill button, display type, generous radii),
 * and a table toolbar with eight controls in a row needs the same brand at a
 * quarter of the scale. What is shared with them is the token layer.
 */

type Tone = "attention" | "confirm" | "info" | "danger";

const FOCUS =
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--admin-accent)]";

const CONTROL = cn(
  "rounded-[var(--admin-radius)] border border-[var(--admin-border-strong)]",
  "bg-[var(--admin-surface)] px-2 py-1 text-sm text-[var(--admin-text)]",
  FOCUS,
);

export const AdminButton = ({
  variant = "secondary",
  className,
  ...props
}: ComponentProps<"button"> & { variant?: "primary" | "secondary" }) => (
  <button
    // Defaulted rather than set at every call site: none of these buttons submit
    // anything, and a stray implicit submit inside a future <form> is a silent bug.
    type="button"
    {...props}
    className={cn(
      "cursor-pointer rounded-[var(--admin-radius)] px-3 py-1.5 text-sm font-medium transition-colors",
      "disabled:cursor-not-allowed disabled:opacity-40",
      FOCUS,
      variant === "primary"
        ? cn(
            "bg-[var(--admin-accent)] text-[var(--admin-accent-contrast)]",
            "hover:bg-[var(--admin-accent-hover)] disabled:hover:bg-[var(--admin-accent)]",
          )
        : cn(
            "border border-[var(--admin-border-strong)] bg-[var(--admin-surface)] text-[var(--admin-text)]",
            "hover:border-[var(--admin-accent)] hover:text-[var(--admin-accent)]",
          ),
      className,
    )}
  />
);

/**
 * A small activity indicator for a control whose write is in flight.
 *
 * Every write on this page goes to the CMS and takes a visible moment — the marker
 * routes re-scan the asset library and republish the registry — so a control that only
 * disabled itself would look broken rather than busy. `aria-hidden` because the state is
 * already announced by the disabled control it sits beside.
 */
export const AdminSpinner = ({
  className,
  ...props
}: ComponentProps<"span">) => (
  <span
    {...props}
    aria-hidden="true"
    className={cn(
      "inline-block size-3.5 shrink-0 animate-spin rounded-full align-middle",
      "border-2 border-[var(--admin-border-strong)] border-t-[var(--admin-accent)]",
      className,
    )}
  />
);

/** Same button at table-row scale — the per-run controls in the history panel. */
export const AdminButtonSmall = ({
  className,
  ...props
}: ComponentProps<typeof AdminButton>) => (
  <AdminButton {...props} className={cn("px-2 py-1 text-xs", className)} />
);

export const AdminSelect = ({
  className,
  ...props
}: ComponentProps<"select">) => (
  <select {...props} className={cn(CONTROL, "cursor-pointer", className)} />
);

export const AdminInput = ({
  className,
  ...props
}: ComponentProps<"input">) => (
  <input
    {...props}
    className={cn(
      CONTROL,
      "placeholder:text-[var(--admin-text-faint)]",
      className,
    )}
  />
);

export const AdminCheckbox = ({
  className,
  ...props
}: ComponentProps<"input">) => (
  <input
    {...props}
    type="checkbox"
    className={cn(
      "size-4 cursor-pointer accent-[var(--admin-accent)]",
      FOCUS,
      className,
    )}
  />
);

/** A label above or beside a control, in the toolbar's own small type. */
export const AdminField = ({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) => (
  <label className={cn("flex items-center gap-2 text-sm", className)}>
    <span className="text-[var(--admin-text-muted)]">{label}</span>
    {children}
  </label>
);

const TONE_VARS: Record<Tone, string> = {
  attention: "border-[var(--admin-attention)] bg-[var(--admin-attention-bg)]",
  confirm: "border-[var(--admin-confirm)] bg-[var(--admin-confirm-bg)]",
  info: "border-[var(--admin-info)] bg-[var(--admin-info-bg)]",
  danger: "border-[var(--admin-danger)] bg-[var(--admin-danger-bg)]",
};

export const AdminNotice = ({
  tone,
  className,
  children,
  ...props
}: ComponentProps<"div"> & { tone: Tone }) => (
  <div
    {...props}
    className={cn(
      "rounded-r-[var(--admin-radius)] border-l-4 p-3 text-sm",
      TONE_VARS[tone],
      className,
    )}
  >
    {children}
  </div>
);

export const AdminBadge = ({
  className,
  variant = "accent",
  ...props
}: ComponentProps<"span"> & { variant?: "accent" | "muted" | Tone }) => (
  <span
    {...props}
    className={cn(
      "inline-block rounded-[var(--admin-radius)] px-2 py-0.5 text-xs",
      variant === "accent" &&
        "bg-[var(--admin-accent)] text-[var(--admin-accent-contrast)]",
      variant === "muted" &&
        "bg-[var(--admin-surface-sunken)] text-[var(--admin-text-muted)]",
      variant === "confirm" &&
        "bg-[var(--admin-confirm-bg)] text-[var(--admin-text)]",
      variant === "attention" &&
        "bg-[var(--admin-attention-bg)] text-[var(--admin-text)]",
      variant === "info" &&
        "bg-[var(--admin-info-bg)] text-[var(--admin-text-muted)]",
      variant === "danger" &&
        "bg-[var(--admin-danger)] text-[var(--admin-text-inverted)]",
      className,
    )}
  />
);

/** One of the counts across the top of the page. */
export const AdminStat = ({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: ReactNode;
  /** Draws the eye to a number that means something is wrong. */
  emphasis?: boolean;
}) => (
  <div>
    <dt className="text-xs tracking-wide text-[var(--admin-text-muted)] uppercase">
      {label}
    </dt>
    <dd
      className={cn(
        "text-2xl font-bold tabular-nums",
        emphasis ? "text-[var(--admin-attention)]" : "text-[var(--admin-text)]",
      )}
      style={{ fontFamily: "var(--admin-font-display)" }}
    >
      {value}
    </dd>
  </div>
);

/** A bordered surface — the history panel, the icon-binding popover. */
export const AdminPanel = ({ className, ...props }: ComponentProps<"div">) => (
  <div
    {...props}
    className={cn(
      "rounded-[var(--admin-radius)] border border-[var(--admin-border)] bg-[var(--admin-surface)] p-3",
      className,
    )}
  />
);
